import { defineStore } from "pinia";
import { TimeslotsApi, type TimeslotAttendeeDTO, SessionDetailsApi, ScheduleApi } from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "./session-details-store";
import { useSessionDetailsAttendeesStore } from "./session-details-attendees-store";
import { useSessionDetailsHabilitationsStore } from "./session-details-habilitations-store";
import type { ISessionsDetailsTimeslotsStore } from "~/utils/interfaces/sessions-interfaces";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { usePagination } from "~/composables/paginations/usePagination";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { isMultiDayTimeslotError, MULTI_DAY_TIMESLOT_MESSAGE } from "~/utils/helpers/sessions/session-timeslots";
import { splitSessionSteps } from "~/utils/helpers/sessions/session-steps";

export const useSessionDetailsTimeslotsStore = defineStore("sessionDetailsTimeslotsStore", {
  state: (): ISessionsDetailsTimeslotsStore => {
    return {
      sessionTimeslots: null,
      sessionAssessments: [],
      currentTimeslot: {
        timeslot: null,
        endTrainingAttendees: null,
        timeslotAttendees: null,
      },
      tmpSessionTimeslots: {
        timeslotId: null,
        sessionTimeslots: null,
      },
      openAccordionAttendees: null,
      showEditTimeslotModalConfirm: null,
      showEditTimeslot: null,
      showAddTimeslot: false,
      sessionSlotsCalendarSync: {
        isLoading: false,
        isError: false,
      },
      icsSend: false,
      isLoadingDeclaration: false,
      isSavingDeclaration: false,
      scheduleConfig: null,
    };
  },
  actions: {
    /**
     * Resolves to whether the timeslots were read: on a failure the previous ones stay, and an answer landing once
     * another session is open is dropped.
     */
    async fetchSessionTimeslots(): Promise<boolean> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      if (sessionDetails.value?.sessionId || sessionDetails.value?.id) {
        const itemId = sessionDetails.value?.id;
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
          const { trainings, assessments } = splitSessionSteps(await apiInstance.getItemTimeslots({ itemId }));
          if (sessionDetails.value?.id !== itemId) return false;
          this.sessionTimeslots = trainings;
          this.sessionAssessments = assessments;
          return true;
        } catch {
          feedback.error(`Une erreur est survenue`, "small");
        }
      }
      return false;
    },
    resetSessionTimeslots() {
      this.sessionTimeslots = null;
      this.sessionAssessments = [];
    },
    async fetchTimeslot(sessionId: string, timeslotId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
      try {
        const { trainings } = splitSessionSteps(await apiInstance.getItemTimeslots({ itemId: sessionId }));
        if (trainings.length > 0) {
          this.currentTimeslot.timeslot = trainings.find((t) => t.timeslotId === timeslotId) ?? null;
        }
      } catch (error) {
        console.error(error);
        this.currentTimeslot.timeslot = null;
      }
    },
    /** `true` once the step is created: a refused one stays in its form to be corrected. */
    async createSessionTimeslots(): Promise<boolean> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      let created = false;
      if (sessionDetails.value?.sessionId || sessionDetails.value?.id) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
          if (this.tmpSessionTimeslots.sessionTimeslots) {
            await apiInstance.createTimeSlot({
              batchCreateTimeslotsDto: {
                createTimeslotsDto: [this.tmpSessionTimeslots.sessionTimeslots],
              },
            });
            // Created even if a refresh below fails: keeping the form open would let a retry create it twice.
            created = true;
            await this.fetchSessionTimeslots();
            await sessionDetailsStore.fetchSessionDetails(sessionDetails.value?.id, sessionDetails.value?.itemType);
            await sessionDetailsStore.fetchSessionAttendeesBasic(
              sessionDetails.value?.id,
              sessionDetails.value?.itemType,
            );

            if (this.scheduleConfig?.gta) {
              feedback.success(
                `Vos modifications ont été sauvegardées
Si vos modifications portent sur des dates ou des horaires, 
pensez à actualiser les indisponibilités`,
                "small",
              );
            } else {
              feedback.success(`Vos modifications ont été sauvegardées`, "small");
            }
          }
        } catch (error) {
          feedback.error(
            isMultiDayTimeslotError(error) ? MULTI_DAY_TIMESLOT_MESSAGE : `Une erreur est survenue lors de la création`,
            "small",
          );
        }
      }
      return created;
    },

    /**
     * Update timeslot data for current session; `true` once saved, as for `createSessionTimeslots`.
     */
    async updateSessionTimeslots(): Promise<boolean> {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      let saved = false;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        if (this.tmpSessionTimeslots.sessionTimeslots && this.tmpSessionTimeslots.timeslotId) {
          await apiInstance.updateTimeSlots({
            timeslotId: this.tmpSessionTimeslots.timeslotId,
            updateTimeslotDto: this.tmpSessionTimeslots.sessionTimeslots,
          });
          saved = true;
          await this.fetchSessionTimeslots();
          if (sessionDetails.value?.sessionId || sessionDetails.value?.id) {
            await sessionDetailsStore.fetchSessionDetails(
              sessionDetails.value?.id ?? sessionDetails.value?.sessionId,
              sessionDetails.value?.itemType,
            );

            if (this.scheduleConfig?.gta) {
              feedback.success(
                `Vos modifications ont été sauvegardées
Si vos modifications portent sur des dates ou des horaires, 
pensez à actualiser les indisponibilités`,
                "small",
              );
            } else {
              feedback.success(`Vos modifications ont été sauvegardées`, "small");
            }
          }
        }
      } catch (error) {
        feedback.error(
          isMultiDayTimeslotError(error)
            ? MULTI_DAY_TIMESLOT_MESSAGE
            : `Une erreur est survenue lors de la modification`,
          "small",
        );
      }
      return saved;
    },

    /**
     * Delete a timeslot from current session
     * @param timeslotId - ID of the timeslot to delete
     */
    async deleteSessionTimeslots(timeslotId: string) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        await apiInstance.removeTimeslot({
          timeslotId,
        });
        await this.fetchSessionTimeslots();
      } catch {
        feedback.error(`Une erreur est survenue lors de la modification`, "small");
      }
    },
    async fetchEndTrainingAttendees(timeslotId: string) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      if (!timeslotId) {
        return null;
      }
      try {
        const data = await apiInstance.getEndTrainingModalAttendees({
          timeslotId,
        });
        if (data) {
          this.currentTimeslot.endTrainingAttendees = data;
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des participants`, "small");
      }
    },
    async endSessionTimeslots() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
      const sessionDetailsHabilitationsStore = useSessionDetailsHabilitationsStore();
      usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
      const BATCH_SIZE = 500;

      this.isSavingDeclaration = true;
      try {
        if (this.currentTimeslot.endTrainingAttendees && this.currentTimeslot.timeslot?.timeslotId) {
          const allAttendees: TimeslotAttendeeDTO[] = this.currentTimeslot.endTrainingAttendees?.map((attendee) => {
            return {
              timeslotAttendeeId: attendee.timeslotAttendeesId,
              attendedHours: attendee.attendedHours,
            } as TimeslotAttendeeDTO;
          });

          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
          const timeslotId = this.currentTimeslot.timeslot?.timeslotId;

          // Traitement par batch pour éviter les timeouts avec un grand nombre de participants
          const totalBatches = Math.ceil(allAttendees.length / BATCH_SIZE);

          for (let i = 0; i < totalBatches; i++) {
            const startIndex = i * BATCH_SIZE;
            const endIndex = Math.min(startIndex + BATCH_SIZE, allAttendees.length);
            const batchAttendees = allAttendees.slice(startIndex, endIndex);

            await apiInstance.endTimeSlotAdmin({
              endTimeslotDto: {
                timeslotId,
                attendees: batchAttendees,
              },
            });
          }

          await this.fetchSessionTimeslots();
          await this.fetchSessionTimeslotsAttendees(timeslotId);
          if (sessionDetails.value?.sessionId || sessionDetails.value?.itemType === ItemType.SESSION) {
            await sessionDetailsStore.fetchSessionAttendeesBasic(
              sessionDetails.value?.sessionId,
              sessionDetails.value?.itemType,
            );
            const paginateAttendees = usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
            const paginationOptionsAttendees = paginateAttendees.getFindOptions(
              paginateAttendees.paginateOptions.value,
            );
            await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
              sessionDetails.value?.sessionId,
              sessionDetails.value?.itemType,
              paginationOptionsAttendees,
            );
            const paginateHabilitations = usePagination(PaginationKeysEnum.HABILITATIONS_PAGINATION);
            const paginationOptionsHabilitations = paginateHabilitations.getFindOptions(
              paginateHabilitations.paginateOptions.value,
            );
            await sessionDetailsHabilitationsStore.fetchSessionAttendeesHabilitations(paginationOptionsHabilitations);
          }
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la modification`, "small");
      } finally {
        this.isSavingDeclaration = false;
      }
    },

    /**
     * Fetch attendees for a specific timeslot
     * @param timeslotId - ID of the timeslot to fetch attendees for
     */
    async fetchSessionTimeslotsAttendees(timeslotId: string, paginationOptions?: Record<string, string>) {
      const feedback = useFeedback();
      let transformQuery = {};

      // Transform pagination options to camelCase or use noLimit
      if (paginationOptions) {
        transformQuery = Object.fromEntries(new URLSearchParams(paginationOptions).entries());
      } else {
        transformQuery = {
          noLimit: true,
        };
      }
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        const timeslotAttendees = await apiInstance.getTimeslotAttendees({ timeslotId, ...transformQuery });
        this.currentTimeslot.timeslotAttendees = timeslotAttendees;
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    async syncCalendarWithTimeSlots(toastIsOff: boolean = false) {
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      this.sessionSlotsCalendarSync.isLoading = true;
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ScheduleApi);

      if (sessionDetails.value?.id && sessionDetails.value?.itemType) {
        try {
          const response = await apiInstance.checkScheduleAvailability({
            itemId: sessionDetails.value?.id,
            itemType: sessionDetails.value?.itemType,
          });

          const checkSyncStatus = async () => {
            if (sessionDetails.value?.id) {
              const statusResponse = await apiInstance.getStatusSyncCalendar({
                itemId: sessionDetails.value?.id,
                itemType: sessionDetails.value?.itemType,
              });

              if (statusResponse.statusSyncOutlook === "success") {
                this.sessionSlotsCalendarSync.isLoading = false;
                clearInterval(timeoutIdSuccess);

                if (this.currentTimeslot.timeslot?.timeslotId) {
                  await this.fetchSessionTimeslotsAttendees(this.currentTimeslot.timeslot?.timeslotId);

                  if (!toastIsOff) {
                    feedback.success("Les disponibilités sont mises à jour", "small");
                  }
                }
              }
            }
          };

          let timeoutIdSuccess: ReturnType<typeof setInterval>;
          if (!response.itemId) {
            timeoutIdSuccess = setInterval(checkSyncStatus, 15000);
          } else {
            this.sessionSlotsCalendarSync.isLoading = false;
          }

          if (this.currentTimeslot.timeslot?.timeslotId && response.lastSyncCalendar) {
            sessionDetails.value.lastSyncCalendar = response.lastSyncCalendar;

            await this.fetchSessionTimeslotsAttendees(this.currentTimeslot.timeslot?.timeslotId);

            if (!toastIsOff) {
              feedback.success("Les disponibilités sont mises à jour", "small");
            }

            this.sessionSlotsCalendarSync.isLoading = false;
          }
        } catch (error: any) {
          if (sessionDetails.value?.id && sessionDetails.value?.itemType && !error.response?.status) {
            const checkSyncStatus = async () => {
              if (sessionDetails.value?.id) {
                const statusResponse = await apiInstance.getStatusSyncCalendar({
                  itemId: sessionDetails.value?.id,
                  itemType: sessionDetails.value?.itemType,
                });
                if (statusResponse.statusSyncOutlook === "success") {
                  this.sessionSlotsCalendarSync.isLoading = false;
                  clearInterval(timeoutId);

                  if (this.currentTimeslot.timeslot?.timeslotId) {
                    await this.fetchSessionTimeslotsAttendees(this.currentTimeslot.timeslot?.timeslotId);

                    if (!toastIsOff) {
                      feedback.success("Les disponibilités sont mises à jour", "small");
                    }
                  }
                }
              }
            };
            const timeoutId = setInterval(checkSyncStatus, 15000);

            setTimeout(() => {
              clearInterval(timeoutId);
              this.sessionSlotsCalendarSync.isLoading = false;
              this.sessionSlotsCalendarSync.isError = true;
              feedback.error("La synchronisation des disponibilités a échoué après 10 minutes", "small");
            }, 600000);
          } else if (!toastIsOff) {
            this.sessionSlotsCalendarSync.isError = true;
            this.sessionSlotsCalendarSync.isLoading = false;
          } else {
            this.sessionSlotsCalendarSync.isLoading = false;
          }
        }
      }
    },
    async checkIcsSend() {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      try {
        if (sessionDetails.value?.sessionId) {
          const data = await apiInstance.getSessionIcsSent({ sessionId: sessionDetails.value?.sessionId });
          if (data) {
            this.icsSend = data.icsSent;
            if (data.icsSent) {
              feedback.success(`Les invitations agenda des participants ont bien été mises à jour`, "small");
            }
          }
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des participants`, "small");
      }
    },
    /**
     * Which availability sources the caller's company has configured (`gta` / `microsoft`).
     *
     * Reads the company off the token, so it is open to every role — unlike
     * `GET /microsoft/company-settings/{companyId}`, which is SIMBEL_ADMIN-only and would 403 here.
     */
    async fetchScheduleConfig() {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, ScheduleApi);
        this.scheduleConfig = await apiInstance.getScheduleConfig();
      } catch (error) {
        // A failure here closes the sync gate, so never let it pass unnoticed: it is otherwise
        // indistinguishable from a company that genuinely has no availability source.
        console.error("Failed to fetch the schedule configuration:", error);
        this.scheduleConfig = null;
      }
    },
  },
});
